import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';

// Patch ResizeObserver to prevent errors from being thrown
// This must happen before any components that use ResizeObserver are loaded
const OriginalResizeObserver = window.ResizeObserver;
window.ResizeObserver = class extends OriginalResizeObserver {
  constructor(callback) {
    const wrappedCallback = (entries, observer) => {
      try {
        callback(entries, observer);
      } catch (error) {
        // Silently ignore ResizeObserver errors
        if (
          error?.message?.includes('ResizeObserver') ||
          error?.toString()?.includes('ResizeObserver')
        ) {
          return;
        }
        throw error;
      }
    };
    super(wrappedCallback);
  }
};

// Suppress ResizeObserver errors in console - these are harmless React development warnings
// that occur when components resize during render cycles
const originalConsoleError = console.error;
console.error = (...args) => {
  const errorString = args
    .map((arg) =>
      typeof arg === 'string'
        ? arg
        : typeof arg === 'object' && arg !== null
          ? JSON.stringify(arg)
          : String(arg)
    )
    .join(' ');

  if (
    errorString.includes('ResizeObserver loop') ||
    errorString.includes('ResizeObserver loop completed') ||
    errorString.includes('ResizeObserver')
  ) {
    return; // Suppress ResizeObserver errors
  }
  originalConsoleError.apply(console, args);
};

// Global error handler to catch ResizeObserver errors before they reach React
// Use capture phase to catch errors as early as possible
window.addEventListener(
  'error',
  (event) => {
    const errorMessage = event.message || event.error?.message || event.error?.toString() || '';
    if (errorMessage.includes('ResizeObserver')) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  },
  true
); // Use capture phase

// Also catch unhandled promise rejections that might be ResizeObserver related
window.addEventListener(
  'unhandledrejection',
  (event) => {
    const errorMessage = event.reason?.message || event.reason?.toString() || '';
    if (errorMessage.includes('ResizeObserver')) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  },
  true
); // Use capture phase

// Suppress React error overlay for ResizeObserver errors
// This intercepts errors before React's error overlay can display them
if (process.env.NODE_ENV === 'development') {
  const originalOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    if (
      (typeof message === 'string' && message.includes('ResizeObserver')) ||
      error?.message?.includes('ResizeObserver') ||
      error?.toString()?.includes('ResizeObserver')
    ) {
      return true; // Suppress the error
    }
    if (originalOnError) {
      return originalOnError(message, source, lineno, colno, error);
    }
    return false;
  };

  // Suppress React DevTools error overlay for ResizeObserver
  // Override the error handler that React DevTools uses
  if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
    const originalEmit = window.__REACT_DEVTOOLS_GLOBAL_HOOK__.emit;
    if (originalEmit) {
      window.__REACT_DEVTOOLS_GLOBAL_HOOK__.emit = function (event, data) {
        if (
          event === 'error' &&
          data &&
          (data.message?.includes('ResizeObserver') ||
            data.toString?.()?.includes('ResizeObserver'))
        ) {
          return; // Suppress ResizeObserver errors in DevTools
        }
        return originalEmit.apply(this, arguments);
      };
    }
  }

  // Additional suppression for React error overlay
  // This catches errors that might slip through to React's error boundary
  const originalErrorHandler = Error.stackTraceLimit;
  Error.stackTraceLimit = Infinity;

  // Override React's error reporting
  if (window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__) {
    const originalOnError = window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__.onError;
    if (originalOnError) {
      window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__.onError = function (err) {
        if (
          err?.message?.includes('ResizeObserver') ||
          err?.toString?.()?.includes('ResizeObserver') ||
          err?.stack?.includes('ResizeObserver')
        ) {
          return; // Suppress ResizeObserver errors
        }
        return originalOnError.apply(this, arguments);
      };
    }
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);
